import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Gavel as GavelIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import api from '../api';

function VirtualCourt() {
  const auth = getAuth();
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [setup, setSetup] = useState(null);
  const [judgeName, setJudgeName] = useState('');
  const [opposingCounselName, setOpposingCounselName] = useState('');
  const [judgeDocuments, setJudgeDocuments] = useState([]);
  const [counselDocuments, setCounselDocuments] = useState([]);
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState(''); // 'judge' or 'counsel'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (selectedCase) {
      fetchSetup();
      fetchDocuments();
      fetchChatHistory();
    }
  }, [selectedCase]);

  const fetchCases = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.get('/cases', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data.cases || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases');
    }
  };

  const fetchSetup = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.get(`/virtual-court/${selectedCase}/setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.setup) {
        setSetup(response.data.setup);
        setJudgeName(response.data.setup.judge_name || '');
        setOpposingCounselName(response.data.setup.opposing_counsel_name || '');
      } else {
        setSetup(null);
        setJudgeName('');
        setOpposingCounselName('');
      }
    } catch (err) {
      console.error('Error fetching setup:', err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.get(`/virtual-court/${selectedCase}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setJudgeDocuments(response.data.judge_documents || []);
      setCounselDocuments(response.data.counsel_documents || []);
      setCaseDocuments(response.data.case_documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const fetchChatHistory = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await api.get(`/virtual-court/${selectedCase}/chat-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setChatHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching chat history:', err);
    }
  };

  const handleSaveSetup = async () => {
    if (!judgeName || !opposingCounselName) {
      setError('Please enter both judge name and opposing counsel name');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await auth.currentUser.getIdToken();
      await api.post(
        `/virtual-court/${selectedCase}/setup`,
        {
          judge_name: judgeName,
          opposing_counsel_name: opposingCounselName
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Virtual court setup saved successfully');
      fetchSetup();
    } catch (err) {
      console.error('Error saving setup:', err);
      setError(err.response?.data?.error || 'Failed to save setup');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleUploadDocuments = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    if (selectedFiles.length > 10) {
      setError('Maximum 10 files allowed');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await auth.currentUser.getIdToken();
      const endpoint = uploadType === 'judge' 
        ? `/virtual-court/${selectedCase}/upload-judge-document`
        : `/virtual-court/${selectedCase}/upload-counsel-document`;

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        await api.post(endpoint, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      setSuccess(`${selectedFiles.length} document(s) uploaded successfully`);
      setSelectedFiles([]);
      setUploadDialogOpen(false);
      fetchDocuments();
    } catch (err) {
      console.error('Error uploading documents:', err);
      setError(err.response?.data?.error || 'Failed to upload documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await auth.currentUser.getIdToken();
      await api.delete(`/virtual-court/${selectedCase}/delete-document/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Document deleted successfully');
      fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.response?.data?.error || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      return;
    }

    if (!setup) {
      setError('Please set up the virtual court first (judge and opposing counsel)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await auth.currentUser.getIdToken();
      // Add user message to UI immediately
      const userMsg = { role: 'user', content: message, timestamp: new Date().toISOString() };
      setChatHistory([...chatHistory, userMsg]);
      
      const currentMessage = message;
      setMessage('');

      const response = await api.post(
        `/virtual-court/${selectedCase}/chat`,
        { message: currentMessage },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Add assistant response to UI
      const assistantMsg = { role: 'assistant', content: response.data.response, timestamp: new Date().toISOString() };
      setChatHistory(prev => [...prev, assistantMsg]);

      // Refresh from server to ensure sync (with small delay for Firestore to process)
      setTimeout(() => {
        fetchChatHistory();
      }, 500);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const openUploadDialog = (type) => {
    setUploadType(type);
    setSelectedFiles([]);
    setUploadDialogOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <GavelIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Virtual Court Simulation
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Case Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Case
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Case</InputLabel>
          <Select
            value={selectedCase}
            onChange={(e) => setSelectedCase(e.target.value)}
            label="Case"
          >
            <MenuItem value="">
              <em>Select a case</em>
            </MenuItem>
            {cases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title} - {c.case_number}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {selectedCase && (
        <>
          {/* Setup Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Virtual Court Setup
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Judge Name"
                  value={judgeName}
                  onChange={(e) => setJudgeName(e.target.value)}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => openUploadDialog('judge')}
                    fullWidth
                  >
                    Upload Judge Documents ({judgeDocuments.length}/10)
                  </Button>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Background, past cases, judicial tendencies, etc.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lead Opposing Counsel Name"
                  value={opposingCounselName}
                  onChange={(e) => setOpposingCounselName(e.target.value)}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => openUploadDialog('counsel')}
                    fullWidth
                  >
                    Upload Counsel Documents ({counselDocuments.length}/10)
                  </Button>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                    Personality, past cases, tactics, etc.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleSaveSetup}
                  disabled={loading || !judgeName || !opposingCounselName}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Setup'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Documents and Chat */}
          <Grid container spacing={3}>
            {/* Documents Panel */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '600px', overflow: 'auto' }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                  <Tab label={<Badge badgeContent={judgeDocuments.length} color="primary">Judge</Badge>} />
                  <Tab label={<Badge badgeContent={counselDocuments.length} color="primary">Counsel</Badge>} />
                  <Tab label={<Badge badgeContent={caseDocuments.length} color="primary">Case</Badge>} />
                </Tabs>

                {tabValue === 0 && (
                  <List>
                    {judgeDocuments.length === 0 ? (
                      <ListItem>
                        <ListItemText 
                          primary="No judge documents uploaded"
                          secondary="Upload documents about the judge's background and past cases"
                        />
                      </ListItem>
                    ) : (
                      judgeDocuments.map((doc) => (
                        <ListItem key={doc.id}>
                          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <ListItemText primary={doc.filename} />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => handleDeleteDocument(doc.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))
                    )}
                  </List>
                )}

                {tabValue === 1 && (
                  <List>
                    {counselDocuments.length === 0 ? (
                      <ListItem>
                        <ListItemText 
                          primary="No counsel documents uploaded"
                          secondary="Upload documents about opposing counsel's tactics and history"
                        />
                      </ListItem>
                    ) : (
                      counselDocuments.map((doc) => (
                        <ListItem key={doc.id}>
                          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <ListItemText primary={doc.filename} />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => handleDeleteDocument(doc.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))
                    )}
                  </List>
                )}

                {tabValue === 2 && (
                  <List>
                    {caseDocuments.length === 0 ? (
                      <ListItem>
                        <ListItemText 
                          primary="No case documents"
                          secondary="Upload documents in the main case page"
                        />
                      </ListItem>
                    ) : (
                      caseDocuments.map((doc) => (
                        <ListItem key={doc.id}>
                          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <ListItemText primary={doc.filename} />
                        </ListItem>
                      ))
                    )}
                  </List>
                )}
              </Paper>
            </Grid>

            {/* Chat Panel */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <ChatIcon sx={{ mr: 1 }} />
                  Strategic Counsel Chat
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {/* Chat History */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
                  {chatHistory.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <ChatIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                      <Typography variant="body1">
                        Start a conversation to get strategic advice
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Ask about case strategy, judge tendencies, opposing counsel tactics, and more
                      </Typography>
                    </Box>
                  ) : (
                    chatHistory.map((msg, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          mb: 2,
                          display: 'flex',
                          justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <Paper
                          sx={{
                            p: 2,
                            maxWidth: '80%',
                            backgroundColor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                            color: msg.role === 'user' ? 'white' : 'text.primary'
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {msg.content}
                          </Typography>
                        </Paper>
                      </Box>
                    ))
                  )}
                </Box>

                {/* Message Input */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Ask about case strategy, actions to take, potential outcomes..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={4}
                    disabled={!setup}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={loading || !message.trim() || !setup}
                    sx={{ minWidth: '100px' }}
                  >
                    {loading ? <CircularProgress size={24} /> : <SendIcon />}
                  </Button>
                </Box>

                {!setup && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Please complete the virtual court setup above before starting a conversation
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload {uploadType === 'judge' ? 'Judge' : 'Opposing Counsel'} Documents
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload up to 10 PDF or Word documents. Current: {uploadType === 'judge' ? judgeDocuments.length : counselDocuments.length}/10
          </Typography>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx"
            onChange={handleFileSelect}
            style={{ display: 'block', marginBottom: '16px' }}
          />
          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files:
              </Typography>
              {selectedFiles.map((file, idx) => (
                <Chip key={idx} label={file.name} sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUploadDocuments} 
            variant="contained"
            disabled={loading || selectedFiles.length === 0}
          >
            {loading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VirtualCourt;
